import Cookies from 'js-cookie'

import {
  parse as defaultParse,
  stringify as defaultStringify,
  type Parser,
  type Stringifier
} from './json'

export type CookieAttributes = Cookies.CookieAttributes

// Modifies default converter to prevent dropping outermost '"' in `read`,
// otherwise JSON.parse can't recover plain strings. Other than that,
// this is copied straight from `js-cookie` internals.
export const cookies: Cookies.CookiesStatic = Cookies.withConverter({
  /**
   * Decodes any percent-encoded sequences in the given string.
   *
   * This replaces all substrings matching a percent-encoding pattern
   * (e.g., "%20", "%3A") with their decoded character equivalents using
   * `decodeURIComponent`.
   */
  read: function (value) {
    return value.replace(/(%[\dA-F]{2})+/gi, decodeURIComponent)
  },
  /**
   * Produces a minimally encoded URI component string.
   *
   * This first encodes the input with `encodeURIComponent`, then selectively
   * decodes certain safe or commonly used characters (e.g., "!", "(", ")", "*",
   * "[", "]", "@") by matching their percent-encoded forms and replacing them
   * with the decoded characters.
   */
  write: function (value) {
    return encodeURIComponent(value).replace(
      /%(2[346BF]|3[AC-F]|40|5[BDE]|60|7[BCD])/g,
      decodeURIComponent
    )
  }
})

/*//////////////////////////////////////////////////////////////
                            COOKIES
//////////////////////////////////////////////////////////////*/

/** @internal */
export function encodeCookie<T>(
  name: string,
  value: T,
  stringify: Stringifier<T> = defaultStringify
) {
  return cookies.converter.write(stringify(value), name)
}

/** @internal */
export function decodeCookie<T = unknown>(
  name: string,
  value: string,
  parse: Parser<T> = defaultParse
) {
  return parse(cookies.converter.read(value, name))
}

/** Reads one cookie out of a raw `Cookie` request header (`k=v; k2=v2`), or `undefined` when absent. */
export function readCookieFromHeader<T = unknown>(
  cookieHeader: string | null | undefined,
  name: string,
  parse: Parser<T> = defaultParse
): T | undefined {
  if (!cookieHeader) return undefined
  const prefix = `${name}=`
  const pair = cookieHeader
    .split(';')
    .map(part => part.trim())
    .find(part => part.startsWith(prefix))
  return pair === undefined ? undefined : decodeCookie(name, pair.slice(prefix.length), parse)
}

/*//////////////////////////////////////////////////////////////
                          LOCALSTORAGE
//////////////////////////////////////////////////////////////*/

export function encodeLocalStorage<T>(
  _name: string,
  value: T,
  stringify: Stringifier<T> = defaultStringify
) {
  return stringify(value)
}

export function decodeLocalStorage<T = unknown>(
  _name: string,
  value: string,
  parse: Parser<T> = defaultParse
) {
  return parse(value)
}
